/**
 * Wayne AI Platform — color tokens.
 *
 * Two layers, in order of authority (post-ADR-001 rebuild):
 *
 * 1. **Forest palette** (Wayne chrome). Editorial / Anthropic-grade aesthetic.
 *    Locked in `FRONTEND_PLAN.md` §2 (L1) and `ONBOARDING_WALKTHROUGH.html`'s
 *    `:root`. Used for the retro-chrome surfaces Tailwind v4 styles via the
 *    CSS-var bridge in `src/styles/globals.css`.
 *
 * 2. **9 agent identity colors** (LOCKED). Each value is verified at WCAG
 *    4.5:1 contrast against the surface tokens for the matching mode in
 *    `MASTER_PLAN.md` §6.2. Light values pass `≥ 4.5:1` against the light
 *    chat surface (`#FFFFFF` and Forest `#FBF9F3`); dark values pass
 *    `≥ 4.5:1` against the dark chat surface (`#0F1419` and Forest `#161A17`).
 *    The `prop` is the colorblind-safe pair — never use color alone.
 *
 * **Do not edit the agent palette without an ADR.** It is asserted by tests
 * in `src/tokens/colors.test.ts` and consumed by the role-isolation property
 * test on the backend.
 */

/** Discriminated union of agent role keys. Matches the backend WS envelope. */
export type AgentId =
  | 'developer'
  | 'manager'
  | 'project-manager'
  | 'qa-testing'
  | 'devops'
  | 'security'
  | 'data-engineer'
  | 'support-engineer'
  | 'analyst';

/** Light vs dark color-scheme key. */
export type ColorMode = 'light' | 'dark';

/**
 * Sprite prop archetype — pairs with color so colorblind users still
 * distinguish agents. See `MASTER_PLAN.md` §6.2.
 */
export type AgentProp =
  | 'laptop'
  | 'blueprint-scroll'
  | 'gold-key'
  | 'clipboard'
  | 'wrench'
  | 'shield'
  | 'book'
  | 'conductor-baton'
  | 'bar-chart';

export interface AgentIdentity {
  readonly id: AgentId;
  readonly displayName: string;
  /** 3-letter mono code used in chat-bubble pointer/tail labels. */
  readonly code: string;
  readonly prop: AgentProp;
  readonly light: string;
  readonly dark: string;
}

/** Locked palette. See `MASTER_PLAN.md` §6.2 — do not edit without an ADR. */
export const AGENTS: Readonly<Record<AgentId, AgentIdentity>> = Object.freeze({
  developer: {
    id: 'developer',
    displayName: 'Developer',
    code: 'DEV',
    prop: 'laptop',
    light: '#1F6FEB',
    dark: '#58A6FF',
  },
  manager: {
    id: 'manager',
    displayName: 'Manager',
    code: 'MGR',
    prop: 'blueprint-scroll',
    light: '#5B3FBF',
    dark: '#A78BFA',
  },
  'project-manager': {
    id: 'project-manager',
    displayName: 'Project Manager',
    code: 'PRJ',
    prop: 'gold-key',
    light: '#5C6B1E',
    dark: '#A8BC4F',
  },
  'qa-testing': {
    id: 'qa-testing',
    displayName: 'QA / Testing',
    code: 'QAT',
    prop: 'clipboard',
    light: '#8A2D6E',
    dark: '#D472B0',
  },
  devops: {
    id: 'devops',
    displayName: 'DevOps',
    code: 'OPS',
    prop: 'wrench',
    light: '#A6541C',
    dark: '#E08A3C',
  },
  security: {
    id: 'security',
    displayName: 'Security',
    code: 'SEC',
    prop: 'shield',
    light: '#B5341A',
    dark: '#F1734A',
  },
  'data-engineer': {
    id: 'data-engineer',
    displayName: 'Data Engineer',
    code: 'DAT',
    prop: 'book',
    light: '#0B6E8C',
    dark: '#4FB6D4',
  },
  'support-engineer': {
    id: 'support-engineer',
    displayName: 'Support Engineer',
    code: 'SUP',
    prop: 'conductor-baton',
    light: '#3A3A3A',
    dark: '#C9C9C9',
  },
  analyst: {
    id: 'analyst',
    displayName: 'Analyst',
    code: 'ANL',
    prop: 'bar-chart',
    light: '#0E7A5F',
    dark: '#3FB68B',
  },
});

/** Stable iteration order matching the LOCKED palette in MASTER_PLAN §6.2. */
export const AGENT_ORDER: readonly AgentId[] = Object.freeze([
  'developer',
  'manager',
  'project-manager',
  'qa-testing',
  'devops',
  'security',
  'data-engineer',
  'support-engineer',
  'analyst',
]);

/** Forest palette — Wayne chrome (FRONTEND_PLAN.md §2 L1). */
export const FOREST = Object.freeze({
  bg: { light: '#F6F3EC', dark: '#0F1311' },
  surface: { light: '#FBF9F3', dark: '#161A17' },
  accent: { light: '#3F5A3A', dark: '#7FA274' },
  ink: { light: '#1B1F1A', dark: '#ECE7D8' },
  ink2: { light: '#3D4239', dark: '#C2BFB2' },
  ink3: { light: '#6D7164', dark: '#8A8C7E' },
  hairline: { light: '#E2DCCB', dark: '#23271F' },
});

/**
 * Cloudscape semantic color names mirrored as plain strings for ad-hoc use
 * outside the global-styles CSS-var bridge. Prefer the Cloudscape CSS
 * variables (`var(--color-text-body-default)` etc.) inside Cloudscape
 * components; these are for convenience in custom atoms.
 */
export const CLOUDSCAPE_NEUTRALS = Object.freeze({
  fafafa: '#FAFAFA',
  f2f3f3: '#F2F3F3',
  ink: '#16191F',
  inkSecondary: '#414D5C',
  link: '#0972D3',
});

/**
 * Helper for components that need an agent's color in the active color mode.
 * Components should pull `mode` from `ThemeContext` (already in `src/contexts`).
 */
export function colorFor(agent: AgentId, mode: ColorMode): string {
  return AGENTS[agent][mode];
}

/**
 * WCAG relative-luminance per the Cloudscape / W3C contrast formula.
 * Returns a number in [0, 1].
 */
export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (c: number): number => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** Computes the WCAG 2 contrast ratio between two hex colors. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

function hexToRgb(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!match) return null;
  const raw = match[1];
  if (!raw) return null;
  const full =
    raw.length === 3
      ? `${raw[0] ?? ''}${raw[0] ?? ''}${raw[1] ?? ''}${raw[1] ?? ''}${raw[2] ?? ''}${raw[2] ?? ''}`
      : raw;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return { r, g, b };
}
